// nodeAncestry.js
//
// Exclusive structural parent (type.parentTypeKey) and stamped node.ancestryPath.

/**
 * normalizeAncestryTypeKey
 *
 * @param {*} value
 * @returns {string}
 */
export function normalizeAncestryTypeKey(value) {
  return String(value || '').trim().toUpperCase();
}

/**
 * normalizeParentTypeKey
 *
 * Nullable exclusive structural parent type on a type definition.
 *
 * @param {*} value
 * @returns {string|null}
 */
export function normalizeParentTypeKey(value) {
  const key = normalizeAncestryTypeKey(value);
  return key || null;
}

/**
 * readTypeParentTypeKey
 *
 * @param {object|null|undefined} typeDef
 * @returns {string|null}
 */
export function readTypeParentTypeKey(typeDef) {
  return normalizeParentTypeKey(typeDef?.parentTypeKey);
}

/**
 * ancestryTypeKeySet
 *
 * Type keys present on a stamped ancestryPath (structural parents).
 *
 * @param {*} value
 * @returns {Set<string>}
 */
export function ancestryTypeKeySet(value) {
  return new Set(normalizeAncestryPath(value).map((entry) => entry.type));
}

/**
 * isTypeInAncestryPath
 *
 * Whether a related type is already represented as a structural parent.
 * Those related tabs are redundant with ancestry breadcrumbs.
 *
 * @param {unknown} typeKey
 * @param {*} ancestryPath
 * @returns {boolean}
 */
export function isTypeInAncestryPath(typeKey, ancestryPath) {
  const type = normalizeAncestryTypeKey(typeKey);
  if (!type) return false;
  return ancestryTypeKeySet(ancestryPath).has(type);
}

/**
 * normalizeAncestryPath
 *
 * @param {*} value
 * @returns {{ type: string, key: string }[]}
 */
export function normalizeAncestryPath(value) {
  if (!Array.isArray(value)) return [];
  const next = [];
  const seen = new Set();
  value.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const type = normalizeAncestryTypeKey(entry.type);
    const key = String(entry.key || '').trim();
    if (!type || !key) return;
    const stamp = `${type}:${key}`;
    if (seen.has(stamp)) return;
    seen.add(stamp);
    next.push({ type, key });
  });
  return next;
}

/**
 * buildAncestryPathFromParent
 *
 * child.ancestryPath = parent.ancestryPath + { parent.type, parent.key }
 *
 * @param {object|null|undefined} parentNode
 * @returns {{ type: string, key: string }[]}
 */
export function buildAncestryPathFromParent(parentNode) {
  const parentKey = String(parentNode?.key || '').trim();
  const parentType = normalizeAncestryTypeKey(parentNode?.type);
  if (!parentKey || !parentType) return [];
  const prior = normalizeAncestryPath(parentNode?.ancestryPath);
  return [...prior, { type: parentType, key: parentKey }];
}

/**
 * isExclusiveParentMatch
 *
 * Whether parentNode is the exclusive structural parent for childTypeDef.
 *
 * @param {object|null|undefined} parentNode
 * @param {object|null|undefined} childTypeDef
 * @returns {boolean}
 */
export function isExclusiveParentMatch(parentNode, childTypeDef) {
  const required = readTypeParentTypeKey(childTypeDef);
  if (!required) return false;
  return normalizeAncestryTypeKey(parentNode?.type) === required;
}

/**
 * stampExclusiveAncestry
 *
 * When child type has parentTypeKey matching parent.type, stamp ancestryPath.
 * Non-exclusive types are returned unchanged (no ancestry cleared).
 *
 * @param {object} childNode
 * @param {object|null|undefined} parentNode
 * @param {object|null|undefined} childTypeDef
 * @returns {object}
 */
export function stampExclusiveAncestry(childNode, parentNode, childTypeDef) {
  if (!childNode || typeof childNode !== 'object') return childNode;
  if (!isExclusiveParentMatch(parentNode, childTypeDef)) return childNode;
  return {
    ...childNode,
    ancestryPath: buildAncestryPathFromParent(parentNode),
  };
}

/**
 * isRelatedTypeAllowedForHost
 *
 * Related Types picker: allow types with no parentTypeKey, or parentTypeKey
 * equal to the host type being edited. Already-selected keys stay visible.
 *
 * @param {object|null|undefined} candidateType
 * @param {string} hostTypeKey
 * @param {string} [selectedTypeKey]
 * @returns {boolean}
 */
export function isRelatedTypeAllowedForHost(candidateType, hostTypeKey, selectedTypeKey = '') {
  const candidateKey = normalizeAncestryTypeKey(candidateType?.key);
  if (!candidateKey) return false;
  const selected = normalizeAncestryTypeKey(selectedTypeKey);
  if (selected && candidateKey === selected) return true;
  const host = normalizeAncestryTypeKey(hostTypeKey);
  const exclusiveParent = readTypeParentTypeKey(candidateType);
  if (!exclusiveParent) return true;
  return exclusiveParent === host;
}
